function bindMenu(menuElement) {
  if (!menuElement || menuElement.dataset.megaMenuBound === '1') {
    return;
  }

  const parentMenuItem = menuElement.closest('li.has-mega-menu');
  const triggerElement = parentMenuItem?.querySelector(':scope > a, :scope > span') ?? null;
  let closeTimerId = null;
  let revealRafId = null;
  let revealCleanupTimerId = null;
  parentMenuItem?.classList.remove('is-mega-open');

  const headerElement = menuElement.closest('header');
  const setMenuInteractive = (isInteractive) => {
    if (isInteractive) {
      menuElement.removeAttribute('inert');
      menuElement.setAttribute('aria-hidden', 'false');
      return;
    }

    menuElement.setAttribute('inert', '');
    menuElement.setAttribute('aria-hidden', 'true');
  };

  const syncHeaderMegaState = () => {
    if (!headerElement) {
      return;
    }

    const hasOpenMegaMenu = headerElement.querySelector('li.has-mega-menu.is-mega-open') !== null;
    headerElement.classList.toggle('has-mega-open', hasOpenMegaMenu);
  };

  const closeOtherMegaMenus = () => {
    document.querySelectorAll('li.has-mega-menu.is-mega-open').forEach((listItem) => {
      if (listItem === parentMenuItem) {
        return;
      }

      listItem.classList.remove('is-mega-open');
      const otherMenuElement = listItem.querySelector(':scope > .mega-menu');
      if (otherMenuElement) {
        otherMenuElement.setAttribute('inert', '');
        otherMenuElement.setAttribute('aria-hidden', 'true');
        otherMenuElement.classList.remove('is-open-reveal');
      }
    });
  };

  const clearCloseTimer = () => {
    if (closeTimerId !== null) {
      window.clearTimeout(closeTimerId);
      closeTimerId = null;
    }
  };

  const clearRevealFrame = () => {
    if (revealRafId !== null) {
      window.cancelAnimationFrame(revealRafId);
      revealRafId = null;
    }
  };

  const clearRevealCleanupTimer = () => {
    if (revealCleanupTimerId !== null) {
      window.clearTimeout(revealCleanupTimerId);
      revealCleanupTimerId = null;
    }
  };

  const replayOpenReveal = () => {
    clearRevealFrame();
    clearRevealCleanupTimer();
    menuElement.classList.remove('is-open-reveal');
    void menuElement.offsetWidth;
    revealRafId = window.requestAnimationFrame(() => {
      menuElement.classList.add('is-open-reveal');
      revealCleanupTimerId = window.setTimeout(() => {
        menuElement.classList.remove('is-open-reveal');
        revealCleanupTimerId = null;
      }, 2200);
      revealRafId = null;
    });
  };

  const isMenuOpen = () => parentMenuItem?.classList.contains('is-mega-open') === true;
  let resetToTopCategory = () => {};

  const openMenu = () => {
    clearCloseTimer();
    const wasOpen = isMenuOpen();
    closeOtherMegaMenus();
    parentMenuItem?.classList.add('is-mega-open');
    setMenuInteractive(true);
    syncHeaderMegaState();
    if (!wasOpen) {
      resetToTopCategory();
      replayOpenReveal();
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerId = window.setTimeout(() => {
      parentMenuItem?.classList.remove('is-mega-open');
      setMenuInteractive(false);
      syncHeaderMegaState();
      closeTimerId = null;
    }, 320);
  };

  setMenuInteractive(false);
  syncHeaderMegaState();

  const buttonElements = Array.from(menuElement.querySelectorAll('[data-mega-category-button]'));
  const panelElements = Array.from(menuElement.querySelectorAll('[data-mega-category-panel]'));

  if (parentMenuItem && parentMenuItem.dataset.megaMenuHoverBound !== '1') {
    if (triggerElement) {
      triggerElement.addEventListener('mouseenter', openMenu);
      triggerElement.addEventListener('mouseleave', scheduleClose);
      triggerElement.addEventListener('focus', openMenu);
      triggerElement.addEventListener('blur', (event) => {
        if (parentMenuItem.contains(event.relatedTarget)) {
          return;
        }

        scheduleClose();
      });
    }

    parentMenuItem.addEventListener('focusout', (event) => {
      if (parentMenuItem.contains(event.relatedTarget)) {
        return;
      }

      scheduleClose();
    });
    parentMenuItem.dataset.megaMenuHoverBound = '1';
  }

  if (headerElement && headerElement.dataset.megaMenuGlobalBound !== '1') {
    headerElement.addEventListener('mouseleave', () => {
      headerElement.querySelectorAll('li.has-mega-menu.is-mega-open').forEach((listItem) => {
        listItem.classList.remove('is-mega-open');
        const openMenuElement = listItem.querySelector(':scope > .mega-menu');
        if (openMenuElement) {
          openMenuElement.setAttribute('inert', '');
          openMenuElement.setAttribute('aria-hidden', 'true');
          openMenuElement.classList.remove('is-open-reveal');
        }
      });

      headerElement.classList.remove('has-mega-open');
    });
    headerElement.dataset.megaMenuGlobalBound = '1';
  }

  menuElement.addEventListener('mouseenter', () => {
    if (!isMenuOpen()) {
      return;
    }

    openMenu();
  });

  menuElement.addEventListener('mouseleave', () => {
    if (!isMenuOpen()) {
      return;
    }

    scheduleClose();
  });

  if (buttonElements.length === 0 || panelElements.length === 0) {
    menuElement.dataset.megaMenuBound = '1';

    return;
  }

  const activateCategory = (categoryKey) => {
    buttonElements.forEach((buttonElement) => {
      buttonElement.classList.toggle(
        'is-active',
        buttonElement.dataset.megaCategoryKey === categoryKey,
      );
    });

    panelElements.forEach((panelElement) => {
      const isActivePanel = panelElement.dataset.megaCategoryKey === categoryKey;
      panelElement.classList.toggle('is-active', isActivePanel);
    });
  };

  const initiallyActiveButton = menuElement.querySelector('[data-mega-category-button].is-active');
  const firstButton = buttonElements[0];
  const initialCategoryKey =
    initiallyActiveButton?.dataset.megaCategoryKey ?? firstButton?.dataset.megaCategoryKey;

  resetToTopCategory = () => {
    const topCategoryKey = buttonElements[0]?.dataset.megaCategoryKey;
    if (!topCategoryKey) {
      return;
    }

    activateCategory(topCategoryKey);
  };

  if (initialCategoryKey) {
    activateCategory(initialCategoryKey);
  }

  buttonElements.forEach((buttonElement) => {
    const categoryKey = buttonElement.dataset.megaCategoryKey;
    if (!categoryKey) {
      return;
    }

    buttonElement.addEventListener('mouseenter', () => activateCategory(categoryKey));
    buttonElement.addEventListener('focus', () => activateCategory(categoryKey));
    buttonElement.addEventListener('click', () => {
      activateCategory(categoryKey);
      openMenu();
    });
  });

  menuElement.dataset.megaMenuBound = '1';
}

export default function initMegaMenu() {
  document.querySelectorAll('[data-mega-menu-module]').forEach((menuElement) => {
    bindMenu(menuElement);
  });
}
